// Shared helpers for the combined "email or phone number" input (Login, Forgot Password).
//
// "+1" is always rendered as a static, non-editable prefix OUTSIDE the actual <input> — the
// editable value only ever holds the local number (or the raw email text). That's what avoids
// the classic masked-input bug: if "+1" lived inside the editable string, React's controlled
// input preserves the caret's raw numeric offset across re-renders, so once reformatting shifted
// characters around, the caret would land back inside "+1" and every keystroke would re-feed
// that literal "1" into the digit stream, corrupting the number (and backspacing to empty would
// get stuck re-adding it). See LoginPage.jsx for where this was first fixed.

// Only digits/space/-/() means the user is typing a phone number, not an email.
export const isPhoneInProgress = (value) => /^[\d\s()-]*$/.test(value);

export const formatPhoneDisplay = (digits) => {
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// The field's stored value is always the full identifier ("+1 650-460-0730" for a phone, or a
// plain email string). These split it back apart for rendering: a static "+1" prefix chip plus
// an editable input holding only the local number.
export const isPhoneIdentifierValue = (value) => /^\+1\s?/.test(value || "");
export const phoneLocalPart = (value) => (value || "").replace(/^\+1\s?/, "");

// Takes the LOCAL (prefix-free) text the user just typed into the input and returns the full
// value to store: auto-detects the US country code and formats as "+1 650-460-0730", e.g.
// "6504600730", "16504600730" and "6504600" (still typing) all work.
export const formatLocalPhoneInput = (localRaw) => {
    let digits = localRaw.replace(/\D/g, "");
    if (!digits) return "";

    if (digits.length > 10 && digits.startsWith("1")) {
        digits = digits.slice(1);
    }
    digits = digits.slice(0, 10);

    return `+1 ${formatPhoneDisplay(digits)}`;
};

// Given the input's PREVIOUS local value (no "+1" prefix) and the raw new value straight from
// the change event, returns what should actually be stored.
//
// This handles the mode-transition moment: digits alone are ambiguous (could still become a
// phone number, or turn out to be the numeric start of an email like "12553@gmail.com"), so
// they get live dash-formatted while typed. The instant a letter/"@" arrives, the dashes already
// baked into the string are OURS, not the user's — if left in, "12553" + "@gmail.com" becomes
// "125-53@gmail.com". So when the new value is just the old (phone-formatted) value with text
// appended, the dashes are stripped back out of the old part before appending, recovering the
// digits the user actually typed.
export const resolveEmailOrPhoneInput = (oldLocalValue, newRawValue) => {
    if (isPhoneInProgress(newRawValue)) {
        return formatLocalPhoneInput(newRawValue);
    }

    if (isPhoneInProgress(oldLocalValue) && newRawValue.startsWith(oldLocalValue)) {
        const appended = newRawValue.slice(oldLocalValue.length);
        return oldLocalValue.replace(/\D/g, "") + appended;
    }

    return newRawValue;
};

// Reformats a previously-saved identifier (e.g. "+16504600730" from localStorage) into the
// display form "+1 650-460-0730". Email values pass through untouched.
export const formatSavedIdentifier = (value) => {
    if (!isPhoneIdentifierValue(value) && !/^\+?\d{10,}$/.test(value || "")) return value;
    return formatLocalPhoneInput(phoneLocalPart(value));
};

// Strips the display formatting back to the clean E.164-style value the backend expects,
// e.g. "+1 650-460-0730" -> "+16504600730". Email values pass through untouched.
export const normalizeIdentifier = (value) => {
    const trimmed = (value || "").trim();
    return isPhoneIdentifierValue(trimmed) ? trimmed.replace(/[\s\-()]/g, "") : trimmed;
};
